import React, {
  useState, useCallback, useMemo,
} from 'react';
import { useSelector } from 'react-redux';
import type { Feature } from 'geojson';

import QueryCard from 'components/Meteorology/Shared/QueryCard';
import QueryCardHeader from 'components/Meteorology/Shared/QueryCard/Header';
import QueryCardTrigger from 'components/Meteorology/Shared/QueryCard/Trigger';
import { PolygonLayerFeatureProperties } from 'components/InteractiveMap/PolygonLayer';
import {
  selectMeteorologyFieldsModelPerformanceMapData,
  selectMeteorologyFieldsModelPerformanceMapLoading,
  selectMeteorologyFieldsModelPerformanceMapError,
} from 'redux/reducers/meteorologyFieldsModelPerformanceReducer';
import { ModelPerformanceMapData } from 'types/Meteorology/Fields/ModelPerformance/mapData';
import usePeriodType from 'hooks/Meteorology/Fields/ModelPerformance/usePeriodType';
import usePentad from 'hooks/Meteorology/Fields/ModelPerformance/usePentad';
import useAnalysisType from 'hooks/Meteorology/Fields/ModelPerformance/useAnalysisType';
import useVariable from 'hooks/Meteorology/Fields/ModelPerformance/useVariable';
import useOptions from 'hooks/Meteorology/Fields/ModelPerformance/useOptions';
import useMapData from 'hooks/Meteorology/Fields/ModelPerformance/useMapData';
import useCanQuery from 'hooks/Meteorology/Fields/ModelPerformance/useCanQuery';
import MapDisplay from './MapDisplay';
import PentadSegmented from './PentadSegmented';
import LegendSheet from './LegendSheet';
import DetailSheet from './DetailSheet';
import ControlPanelSheet from './ControlPanelSheet';

import './index.scss';

function isModelPerformanceMapData(data: unknown): data is ModelPerformanceMapData {
  return (
    typeof data === 'object'
    && data !== null
    && 'geoJson' in data
    && 'legend' in data
    && 'variable' in data
    && 'unit' in data
  );
}

export default function ModelPerformanceMobile() {
  const [panelOpen, setPanelOpen] = useState(false);
  const [legendOpen, setLegendOpen] = useState(false);
  const [selectedFeature, setSelectedFeature] = useState<PolygonLayerFeatureProperties | null>(
    null,
  );

  const mapData = useSelector(selectMeteorologyFieldsModelPerformanceMapData);
  const mapLoading = useSelector(selectMeteorologyFieldsModelPerformanceMapLoading);
  const mapError = useSelector(selectMeteorologyFieldsModelPerformanceMapError);

  const { periodType } = usePeriodType();
  const { pentad } = usePentad();
  const { analysisType } = useAnalysisType();
  const { variable } = useVariable();
  const { analysisTypes, variables, pentads } = useOptions();
  const { triggerMapDataRequest } = useMapData();
  const { canQuery } = useCanQuery();

  const hasValidData = isModelPerformanceMapData(mapData) && mapData.geoJson?.features?.length > 0;
  const showInstructions = !hasValidData && !mapLoading && !mapError;

  const queryTitle = useMemo(() => {
    const variableLabel = variables.find((item) => item.id === variable)?.label;
    const analysisTypeLabel = analysisTypes.find((item) => item.id === analysisType)?.label;
    if (!variableLabel || !analysisTypeLabel) return '';
    return `${variableLabel} · ${analysisTypeLabel}`;
  }, [variables, variable, analysisTypes, analysisType]);

  const querySubtitle = useMemo(() => {
    if (!periodType) return '';
    const periodTypeLabel = periodType === 'preset' ? 'Pré-definido' : 'Livre';
    if (periodType === 'free') return `${periodTypeLabel} · período livre`;
    const pentadLabel = pentads.find((item) => item.id === pentad)?.label;
    return pentadLabel ? `${periodTypeLabel} · ${pentadLabel}` : '';
  }, [periodType, pentads, pentad]);

  const onPolygonClick = useCallback(
    (_feature: Feature, properties: PolygonLayerFeatureProperties) => {
      setSelectedFeature(properties);
    },
    [],
  );

  const clearSelectedFeature = useCallback(() => {
    setSelectedFeature(null);
  }, []);

  return (
    <div className="mp-mobile">
      <QueryCard>
        {hasValidData && (queryTitle || querySubtitle) && (
          <QueryCardHeader label="Consulta atual" title={queryTitle} subtitle={querySubtitle} />
        )}
        <PentadSegmented />
        <QueryCardTrigger onClick={() => setPanelOpen(true)} />
      </QueryCard>

      {showInstructions && (
        <div className="mp-mobile-instructions">
          {canQuery ? (
            <>
              <p className="mp-mobile-instructions-title">Pronto para uma nova consulta</p>
              <p className="mp-mobile-instructions-body">
                As opções já configuradas continuam válidas. Toque para atualizar o mapa
                com a seleção atual.
              </p>
              <button
                type="button"
                className="mp-mobile-instructions-cta"
                onClick={() => triggerMapDataRequest()}
              >
                Realizar consulta novamente
              </button>
            </>
          ) : (
            <>
              <p className="mp-mobile-instructions-title">Configure e realize a consulta</p>
              <p className="mp-mobile-instructions-body">
                Escolha período, tipo de análise e variável no painel de controle para gerar o
                mapa de desempenho.
              </p>
              <button
                type="button"
                className="mp-mobile-instructions-cta"
                onClick={() => setPanelOpen(true)}
              >
                Abrir painel de controle
              </button>
            </>
          )}
        </div>
      )}

      {!showInstructions && (
        <MapDisplay
          mapData={hasValidData ? (mapData as ModelPerformanceMapData) : null}
          mapLoading={mapLoading}
          mapError={mapError}
          selectedFeatureName={selectedFeature?.name ?? null}
          onPolygonClick={onPolygonClick}
          onClearSelection={clearSelectedFeature}
          onOpenLegend={() => setLegendOpen(true)}
        />
      )}

      <ControlPanelSheet open={panelOpen} dismiss={() => setPanelOpen(false)} />

      {hasValidData && (
        <LegendSheet
          open={legendOpen}
          dismiss={() => setLegendOpen(false)}
          legend={(mapData as ModelPerformanceMapData).legend}
          variable={(mapData as ModelPerformanceMapData).variable}
          unit={(mapData as ModelPerformanceMapData).unit}
        />
      )}

      <DetailSheet
        open={selectedFeature != null}
        dismiss={clearSelectedFeature}
        feature={selectedFeature}
        legend={hasValidData ? (mapData as ModelPerformanceMapData).legend : []}
      />
    </div>
  );
}
